import React, { useState, useEffect } from 'react';
import { StyleSheet, Text, View, Pressable, Image, Linking } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import DataOps from '../../../../data/DataOps';

interface CampCoachCardProps {
    coachId: string;
    campId: string;
    isEditing: boolean;
    onRemove: (coachId: string) => void;
}

const CampCoachCard: React.FC<CampCoachCardProps> = ({
    coachId,
    campId,
    isEditing,
    onRemove,
}) => {
    const [coach, setCoach] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    useEffect(() => {
        const fetchCoach = async () => {
            try {
                // Try to fetch from coach collection first
                let coachData = await DataOps.fetchDataFromFirebase('coach', coachId);

                // If not found in coach collection, try host collection (hosts can be coaches too)
                if (!coachData) {
                    coachData = await DataOps.getHostAsCoach(coachId);
                }

                setCoach(coachData);
            } catch (error) {
                console.error('Error fetching coach:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCoach();
    }, [coachId]);

    const handleRemove = async () => {
        try {
            const success = await DataOps.unlinkCoachFromCamp(campId, coachId);
            if (success) {
                onRemove(coachId);
            } else {
                alert('Failed to remove coach. Please try again.');
            }
        } catch (error) {
            console.error('Error removing coach:', error);
            alert('Failed to remove coach. Please try again.');
        }
        setShowConfirmDelete(false);
    };

    if (isLoading) {
        return (
            <View style={styles.card}>
                <Text style={styles.subtleText}>Loading coach information...</Text>
            </View>
        );
    }

    if (!coach) {
        return (
            <View style={styles.card}>
                <Text style={styles.errorText}>Coach not found</Text>
            </View>
        );
    }

    const displayName = `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || 'Coach';
    const isHost = coach.isHost || coach.accountType === 'host';
    const initials = `${(coach.firstName || '').trim().charAt(0)}${(coach.lastName || '').trim().charAt(0)}`.toUpperCase() || 'C';
    const profileImage = coach.profileImage as string | undefined;

    const onEmailPress = () => {
        if (!coach.email) return;
        try { Linking.openURL(`mailto:${coach.email}`); } catch { }
    };

    const onPhonePress = () => {
        if (!coach.phone) return;
        try { Linking.openURL(`tel:${coach.phone}`); } catch { }
    };

    // Stats with safe fallbacks and legacy support keys
    const yearsOfExperience: number = (coach.yearsOfExperience ?? coach.experience ?? 0) as number;
    const studentsTrained: number = (coach.studentsTrained ?? coach.students ?? 0) as number;
    const ratingsCount: number = (coach.ratingsCount ?? coach.ratingCount ?? coach.numRatings ?? 0) as number;

    return (
        <View style={styles.card}>
            {isEditing && (
                <Pressable
                    style={styles.removeButton}
                    onPress={(e: any) => { e?.stopPropagation?.(); setShowConfirmDelete(true); }}
                >
                    <MaterialCommunityIcons name="close-circle" size={22} color="#FF5252" />
                </Pressable>
            )}

            <View style={styles.headerRow}>
                <View style={styles.avatarOuter}>
                    {profileImage ? (
                        <Image source={{ uri: profileImage }} style={styles.avatarImage} />
                    ) : (
                        <View style={[styles.avatarFallback, { backgroundColor: isHost ? '#E3F2FD' : '#FFF3E0' }]}>
                            <Text style={[styles.avatarInitials, { color: isHost ? '#1976D2' : '#E65100' }]}>{initials}</Text>
                        </View>
                    )}
                </View>
                <View>
                    <View style={styles.nameRow}>
                        <Text style={styles.name}>{displayName}</Text>
                    </View>
                    <View style={styles.hostBadge}>
                        <Text style={styles.hostBadgeText}>{isHost ? 'Host' : 'Coach'}</Text>
                    </View>
                </View>
            </View>

            <View style={styles.statsGrid}>
                <View style={styles.statItem}>
                    <View style={styles.statHeader}>
                        <MaterialCommunityIcons name="badge-account-outline" size={16} color="#374151" />
                        <Text style={styles.statNumber}>{yearsOfExperience}</Text>
                    </View>
                    <Text style={styles.statLabel}>yrs experience</Text>
                </View>
                <View style={styles.statItem}>
                    <View style={styles.statHeader}>
                        <MaterialCommunityIcons name="account-group" size={16} color="#374151" />
                        <Text style={styles.statNumber}>{studentsTrained}</Text>
                    </View>
                    <Text style={styles.statLabel}>students trained</Text>
                </View>
                <View style={styles.statItem}>
                    <View style={styles.statHeader}>
                        <MaterialCommunityIcons name="star" size={16} color="#F59E0B" />
                        <Text style={styles.statNumber}>{ratingsCount}</Text>
                    </View>
                    <Text style={styles.statLabel}>ratings</Text>
                </View>
            </View>

            <View style={styles.chipsRow}>
                {coach.email && (
                    <Pressable style={styles.chip} onPress={(e: any) => { e?.stopPropagation?.(); onEmailPress(); }}>
                        <MaterialCommunityIcons name="email" size={14} color="#1F2937" />
                        <Text numberOfLines={1} style={styles.chipText}>{coach.email}</Text>
                    </Pressable>
                )}
                {coach.phone && (
                    <Pressable style={styles.chip} onPress={(e: any) => { e?.stopPropagation?.(); onPhonePress(); }}>
                        <MaterialCommunityIcons name="phone" size={14} color="#1F2937" />
                        <Text numberOfLines={1} style={styles.chipText}>{coach.phone}</Text>
                    </Pressable>
                )}
            </View>

            {showConfirmDelete && (
                <View style={styles.confirmOverlay}>
                    <View style={styles.confirmBox}>
                        <Text style={styles.confirmText}>Remove {displayName} as coach?</Text>
                        <View style={styles.confirmButtons}>
                            <Pressable
                                style={[styles.confirmButton, styles.cancelButton]}
                                onPress={() => setShowConfirmDelete(false)}
                            >
                                <Text style={styles.cancelButtonText}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={[styles.confirmButton, styles.deleteButton]}
                                onPress={handleRemove}
                            >
                                <Text style={styles.deleteButtonText}>Remove</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    card: {
        position: 'relative',
        backgroundColor: '#ffffff',
        borderRadius: 14,
        padding: 16,
        borderWidth: 1,
        borderColor: '#ECECEC',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
        elevation: 2,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
    },
    avatarOuter: {
        width: 56,
        height: 56,
        borderRadius: 28,
        marginRight: 12,
        overflow: 'hidden',
        backgroundColor: '#F5F5F5',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarImage: {
        width: '100%',
        height: '100%',
        borderRadius: 28,
    },
    avatarFallback: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarInitials: {
        fontSize: 18,
        fontWeight: '700',
    },
    nameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    name: {
        fontSize: 18,
        fontWeight: '600',
        color: '#222',
        marginRight: 8,
    },
    subtitle: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: -4,
    },
    hostBadge: {
        alignSelf: 'flex-start',
        backgroundColor: '#3C8248',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
        textTransform: 'uppercase',
    },
    hostBadgeText: {
        fontSize: 11,
        fontWeight: '600',
        color: '#fff',
    },
    statsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        backgroundColor: '#FAFAFA',
        borderWidth: 1,
        borderColor: '#F1F1F1',
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 12,
    },
    statItem: {
        width: '50%',
        paddingVertical: 6,
    },
    statHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 2,
    },
    statNumber: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
    },
    statLabel: {
        fontSize: 12,
        color: '#6B7280',
    },
    chipsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
        maxWidth: '100%',
    },
    chipText: {
        fontSize: 12,
        color: '#1F2937',
        maxWidth: 180,
    },
    removeButton: {
        position: 'absolute',
        top: 8,
        right: 8,
        padding: 4,
    },
    subtleText: {
        fontSize: 14,
        color: '#999',
        textAlign: 'center',
    },
    errorText: {
        fontSize: 14,
        color: '#FF5252',
        textAlign: 'center',
    },
    confirmOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 12,
    },
    confirmBox: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 20,
        width: '80%',
        maxWidth: 300,
    },
    confirmText: {
        fontSize: 16,
        color: '#222',
        textAlign: 'center',
        marginBottom: 20,
    },
    confirmButtons: {
        flexDirection: 'row',
        gap: 12,
    },
    confirmButton: {
        flex: 1,
        paddingVertical: 10,
        borderRadius: 6,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#F0F0F0',
    },
    cancelButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#666',
    },
    deleteButton: {
        backgroundColor: '#FF5252',
    },
    deleteButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#fff',
    },
});

export default CampCoachCard;

